import React, { useState, useEffect } from "react";
import { FiSearch, FiRefreshCw, FiCheckCircle, FiXCircle, FiClock, FiTrendingUp, FiUser, FiChevronRight } from "react-icons/fi";
import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";
import AttendanceDetail from "../detail/AttendanceDetail";

export default function AttendanceTab() {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [globalSummary, setGlobalSummary] = useState({ compliance: 0, present: 0, absent: 0, late: 0 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const pageSizeOptions = [
    { label: "5 / page", value: 5 },
    { label: "10 / page", value: 10 },
    { label: "20 / page", value: 20 },
    { label: "50 / page", value: 50 }
  ];

  const fetchAttendanceReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("page_size", itemsPerPage);
      if (search) params.append("search", search);

      const response = await api.get(`/attendance/report/?${params.toString()}`);
      const data = response.data;

      setReportData(data.results || []);
      setTotalRecords(data.total_records || 0);
      if (data.global_summary) setGlobalSummary(data.global_summary);
    } catch (error) {
      console.error("Error fetching attendance report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceReport();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchAttendanceReport();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // If an employee row was clicked, render AttendanceDetail
  if (selectedEmployee) {
    return (
      <AttendanceDetail
        employee={selectedEmployee}
        onBack={() => setSelectedEmployee(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex items-center justify-between gap-3 p-1">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search employee by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
          />
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                <th className="py-3 px-4">Employee</th>
                <th className="py-3 px-4">ID / Position Ref</th>
                <th className="py-3 px-4 text-center">Present</th>
                <th className="py-3 px-4 text-center">Absent</th>
                <th className="py-3 px-4 text-center">Late</th>
                <th className="py-3 px-4 text-right">Total Days</th>
                <th className="py-3 px-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                    Loading attendance report...
                  </td>
                </tr>
              ) : reportData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    No attendance report found matching search query.
                  </td>
                </tr>
              ) : (
                reportData.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedEmployee(row)}
                    className="hover:bg-zinc-50/80 transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-4 font-semibold text-zinc-900">
                      <div className="flex items-center gap-2">
                        <span>{row.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-[11px] text-zinc-500">
                      {row.position?.id_number || `EMP-${row.id}`}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
                        {row.present}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700">
                        {row.absent}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
                        {row.late}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-zinc-900">
                      {row.total}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <FiChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalCount={totalRecords}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </div>
  );
}